import googleSheetsLogo from '../assets/integrations/google-sheets.svg';
import hubspotLogo from '../assets/integrations/hubspot.svg';
import makeLogo from '../assets/integrations/make.svg';
import microsoftTeamsLogo from '../assets/integrations/microsoft-teams.svg';
import n8nLogo from '../assets/integrations/n8n.svg';
import salesforceLogo from '../assets/integrations/salesforce.svg';
import slackLogo from '../assets/integrations/slack.svg';
import zapierLogo from '../assets/integrations/zapier.svg';
import zohoLogo from '../assets/integrations/zoho.svg';

/**
 * Every tool the Integrations page lists (`features/integrations`).
 *
 * - `native`: built into Groundly, with its own view to connect and manage it.
 * - `webhook`: works today through a Groundly webhook; its view is a short
 *   setup guide that ends in "Add webhook".
 * - `soon`: planned, shown so users can see what's coming. It has no view.
 *
 * Logos are the vendors' own marks, unaltered (svgl.app and Simple Icons,
 * CC0), bundled so the page never loads a third-party image. Webhooks has no
 * vendor, so it uses a Lucide icon instead (`IntegrationLogo`).
 */
export type IntegrationCategory = 'chat' | 'crm' | 'automation' | 'spreadsheet' | 'developer';

export const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  chat: 'Team chat',
  crm: 'CRM',
  automation: 'Automation',
  spreadsheet: 'Spreadsheets',
  developer: 'Developer',
};

/** The order categories are listed in. */
export const CATEGORY_ORDER: readonly IntegrationCategory[] = [
  'chat',
  'crm',
  'automation',
  'spreadsheet',
  'developer',
];

/** An integration that has a view on this page (`?integration=`). */
export type IntegrationId =
  'slack' | 'hubspot' | 'salesforce' | 'zoho' | 'webhooks' | 'zapier' | 'make' | 'n8n';

interface BaseIntegration {
  name: string;
  category: IntegrationCategory;
  /** One line for the catalog: what the user gets. */
  summary: string;
  /** A bundled logo URL; `null` draws the Webhooks icon. */
  logo: string | null;
}

export interface OpenIntegration extends BaseIntegration {
  id: IntegrationId;
  availability: 'native' | 'webhook';
}

export interface PlannedIntegration extends BaseIntegration {
  id: string;
  availability: 'soon';
}

export type Integration = OpenIntegration | PlannedIntegration;

/** A tool that works through a webhook: what to do in that tool. */
export interface WebhookGuide {
  /** Where to find the webhook address in the tool, step by step. */
  steps: readonly string[];
  /** What the tool calls the address, used in the field placeholder. */
  placeholder: string;
}

export const INTEGRATIONS: readonly Integration[] = [
  {
    id: 'slack',
    availability: 'native',
    name: 'Slack',
    category: 'chat',
    summary: 'Post each new lead to a Slack channel, with a link to the conversation.',
    logo: slackLogo,
  },
  {
    id: 'microsoft-teams',
    availability: 'soon',
    name: 'Microsoft Teams',
    category: 'chat',
    summary: 'Post each new lead to a Teams channel.',
    logo: microsoftTeamsLogo,
  },
  {
    id: 'hubspot',
    availability: 'native',
    name: 'HubSpot',
    category: 'crm',
    summary: 'Create or update a HubSpot contact for each new lead.',
    logo: hubspotLogo,
  },
  {
    id: 'salesforce',
    availability: 'native',
    name: 'Salesforce',
    category: 'crm',
    summary: 'Add each new lead to Salesforce as a lead record.',
    logo: salesforceLogo,
  },
  {
    id: 'zoho',
    availability: 'native',
    name: 'Zoho CRM',
    category: 'crm',
    summary: 'Add each new lead to Zoho CRM.',
    logo: zohoLogo,
  },
  {
    id: 'zapier',
    availability: 'webhook',
    name: 'Zapier',
    category: 'automation',
    summary: 'Start a Zap with each new lead and send it to thousands of apps.',
    logo: zapierLogo,
  },
  {
    id: 'make',
    availability: 'webhook',
    name: 'Make',
    category: 'automation',
    summary: 'Start a Make scenario with each new lead.',
    logo: makeLogo,
  },
  {
    id: 'n8n',
    availability: 'webhook',
    name: 'n8n',
    category: 'automation',
    summary: 'Start an n8n workflow with each new lead, in the cloud or self-hosted.',
    logo: n8nLogo,
  },
  {
    id: 'google-sheets',
    availability: 'soon',
    name: 'Google Sheets',
    category: 'spreadsheet',
    summary: 'Add a row to a spreadsheet for each new lead.',
    logo: googleSheetsLogo,
  },
  {
    id: 'webhooks',
    availability: 'native',
    name: 'Webhooks',
    category: 'developer',
    summary: 'Send each new lead to your own address as a signed JSON request.',
    logo: null,
  },
];

export const WEBHOOK_GUIDES: Record<'zapier' | 'make' | 'n8n', WebhookGuide> = {
  zapier: {
    steps: [
      'In Zapier, create a Zap and choose Webhooks by Zapier as the trigger, with the Catch Hook event.',
      'Copy the webhook URL Zapier shows you.',
      'Add a webhook here with that URL, then send a test event so Zapier can see a lead.',
      'Back in Zapier, add the actions you want, such as creating a contact in your CRM.',
    ],
    placeholder: 'https://hooks.zapier.com/hooks/catch/…',
  },
  make: {
    steps: [
      'In Make, create a scenario and start it with the Webhooks module’s Custom webhook trigger.',
      'Add a webhook in that module and copy the address Make gives you.',
      'Add a webhook here with that address, then send a test event so Make can learn the lead’s fields.',
      'Back in Make, add the modules you want after the trigger.',
    ],
    placeholder: 'https://hook.eu1.make.com/…',
  },
  n8n: {
    steps: [
      'In n8n, start a workflow with the Webhook node and set its HTTP method to POST.',
      'Copy the node’s production URL, and activate the workflow so that URL listens.',
      'Add a webhook here with that URL, then send a test event to check it arrives.',
      'Add the nodes you want after the Webhook node.',
    ],
    placeholder: 'https://your-n8n.example.com/webhook/…',
  },
};

export type WebhookTool = keyof typeof WEBHOOK_GUIDES;

/**
 * The tool a webhook address belongs to, read from its host, so a Zapier
 * webhook is listed with Zapier's logo. `null` for anything else, including a
 * self-hosted n8n on a host without "n8n" in its name.
 */
export function toolForUrl(url: string): WebhookTool | null {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  const isOrUnder = (domain: string) => host === domain || host.endsWith(`.${domain}`);
  if (isOrUnder('zapier.com')) return 'zapier';
  if (isOrUnder('make.com') || isOrUnder('integromat.com')) return 'make';
  if (host.split('.').some((label) => label === 'n8n' || label.startsWith('n8n-'))) return 'n8n';
  if (isOrUnder('n8n.cloud')) return 'n8n';
  return null;
}

const OPEN_IDS: readonly string[] = [
  'slack',
  'hubspot',
  'salesforce',
  'zoho',
  'webhooks',
  'zapier',
  'make',
  'n8n',
];

export function isIntegrationId(value: unknown): value is IntegrationId {
  return typeof value === 'string' && OPEN_IDS.includes(value);
}

export function integrationById(id: IntegrationId): OpenIntegration {
  const found = INTEGRATIONS.find((i): i is OpenIntegration => i.id === id);
  if (found === undefined) throw new Error(`Unknown integration ${id}`);
  return found;
}
